import type { Answers } from "@/schema/quizSchema";
import {
  presentationScore, presentationBand, exposureScore, exposureBand, windowMath,
  type PresentationBand, type ExposureBand, type WindowMath,
} from "./scoring";
import { BRAND_LABEL, PLAN_FOCUS } from "./copy";

export interface RootCause { name: string; copy: string }
export type FragranceState = "CONFIRMED" | "CLEAN" | "UNVERIFIED";

export interface HormoneProfile {
  presentation: PresentationBand;
  exposure: ExposureBand;
  presentationScore: number;
  exposureScore: number;
  window: WindowMath;
  primary: RootCause;
  secondary: RootCause;
  fragrance: { state: FragranceState; copy: string };
  brandLabel: string;
  planFocus: string;
  /** Plain-language names for what she reported, used in the assessment. */
  observed: string[];
  sawDoctor: boolean;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const list = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

const FRAGRANCE: Record<string, { state: FragranceState; copy: string }> = {
  found: {
    state: "CONFIRMED",
    copy: "You found it yourself. One word, 3,300+ possible ingredients behind it, and no law requiring them to tell you which.",
  },
  clean: {
    state: "CLEAN",
    copy: "Not on this one. That puts you ahead of most parents who take this, so the other products are where to look next.",
  },
  later: {
    state: "UNVERIFIED",
    copy: "Worth the thirty seconds tonight. Third from the bottom is where it usually sits.",
  },
};

/**
 * Pure. Maps a completed answer set to the full result-page profile.
 *
 * The two axes are derived INDEPENDENTLY and never combined into a single
 * verdict, because combining them would imply the causal link the result page
 * explicitly refuses to claim. Nothing in here says the exposure produced the
 * presentation.
 */
export function deriveProfile(answers: Answers): HormoneProfile {
  const pScore = presentationScore(answers);
  const eScore = exposureScore(answers);
  const brand = str(answers.brand);
  const brandLabel = BRAND_LABEL[brand] ?? "his current wash";

  const extras = list(answers.products).filter((v) => v !== "justwash");
  const duration = str(answers.duration);
  const hot = str(answers.heat) === "steam";

  // Primary: the mechanism of the bottle she actually named.
  const primary: RootCause =
    brand === "natural"
      ? {
          name: "An unregulated label",
          copy: `${brandLabel} is not held to any definition of "natural". If "fragrance" is on the back, the same synthetics are in the bottle.`,
        }
      : {
          name: "Synthetic scent carrier",
          copy: `${brandLabel}. Phthalates are what makes scent stick, and "fragrance" is the one line on a US label where they are allowed to sit unnamed.`,
        };

  // Secondary: the strongest amplifier she earned, with a backfill so the grid
  // never renders a hole.
  let secondary: RootCause;
  if (hot) {
    secondary = {
      name: "Heat and thin skin",
      copy: "Hot water opens the barrier on his scalp, underarms and groin. The thinnest skin he has, and exactly where he soaps.",
    };
  } else if (extras.length >= 4) {
    secondary = {
      name: "Stacked load",
      copy: "Five products before breakfast, each one carrying its own undisclosed scent formula.",
    };
  } else if (duration === "3to4" || duration === "since") {
    secondary = {
      name: "Duration",
      copy: "Years of daily contact, straight through the window that builds him.",
    };
  } else {
    secondary = {
      name: "Daily contact",
      copy: "Every morning, on the thinnest skin he has, during the years his endocrine system is calibrating.",
    };
  }

  // Plain-language names for what she reported, in screen order.
  const observed: string[] = [];
  if (["done", "flat", "resurfaces"].includes(str(answers.energy))) observed.push("his energy");
  if (["behind", "abit"].includes(str(answers.build))) observed.push("how he is filling out");
  if (["bothers", "babyfat", "maybe"].includes(str(answers.chest))) observed.push("his chest");
  if (["quiet", "both", "shortfuse", "some"].includes(str(answers.drive))) {
    observed.push("how he has been with you");
  }
  if (["hour", "never", "schoolday", "physical"].includes(str(answers.odor))) {
    observed.push("how fast the smell comes back");
  }

  return {
    presentation: presentationBand(pScore),
    exposure: exposureBand(eScore),
    presentationScore: pScore,
    exposureScore: eScore,
    window: windowMath(str(answers.age)),
    primary,
    secondary,
    fragrance: FRAGRANCE[str(answers.bottle)] ?? FRAGRANCE.later,
    brandLabel,
    planFocus: PLAN_FOCUS[str(answers.want)] ?? PLAN_FOCUS.clean,
    observed,
    sawDoctor: ["boysvary", "bloodwork"].includes(str(answers.doctor)),
  };
}
